"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import VerifyClinicModal from "./VerifyClinicModal";
import RejectClinicModal from "./RejectClinicModal";
import "./ClinicDetailModal.css";

const ClinicDetailModal = ({ isOpen, onClose, clinic, onVerify, onReject, onUpgradeSubscription, onDowngradeSubscription }) => {
  const { t } = useTranslation();
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  // Use clinic data directly from the list response
  // Note: The backend doesn't support GET /api/v1/clinics/{id}, so we rely on
  // the list endpoint to return all necessary fields. If fields are missing,
  // the backend's /api/v1/clinics endpoint needs to be updated to include them.
  const displayClinic = clinic || {};
  
  
  const isUnverified = displayClinic.status === 'unverified';
  const isVerified = displayClinic.status === 'verified';
  const isRejected = displayClinic.status === 'rejected';
  
  // Check subscription plan
  const subscriptionPlan = 
    displayClinic.clinic_subscription?.plan ||
    displayClinic.clinic_subscription_plan;
  const isPaid = subscriptionPlan === 'paid';
  
  // Check if clinic has requested upgrade (only show upgrade button if they have)
  const hasUpgradeRequest = 
    displayClinic.clinic_subscription?.upgrade_requested === true ||
    displayClinic.clinic_subscription?.has_upgrade_request === true ||
    displayClinic.upgrade_requested === true ||
    displayClinic.has_upgrade_request === true;

  const handleVerify = () => {
    setShowVerifyModal(true);
  };

  const handleReject = () => {
    setShowRejectModal(true);
  };

  const handleVerifyConfirm = () => {
    onVerify(displayClinic.id);
    setShowVerifyModal(false);
  };

  const handleRejectConfirm = (reason) => {
    onReject(displayClinic.id, reason);
    setShowRejectModal(false);
  };

  if (!isOpen) return null;
  if (!clinic) return null;

  return (
    <div className="clinic-detail-modal-overlay" onClick={onClose}>
      <div className="clinic-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="clinic-detail-modal-header">
          <h2 className="clinic-detail-modal-title">
            {displayClinic.name_en || displayClinic.name_ar || "Clinic Name Not Available"}
            {displayClinic.name_ar && displayClinic.name_en && (
              <span className="clinic-detail-modal-title-ar"> ({displayClinic.name_ar})</span>
            )}
          </h2>
          <button className="clinic-detail-modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="clinic-detail-modal-content">
          {displayClinic.logo && (
            <div className="clinic-detail-modal-section">
              <h3 className="clinic-detail-modal-section-title">{t("adminDashboard.modal.sections.logo")}</h3>
              <div className="clinic-detail-modal-logo">
                <img 
                  src={displayClinic.logo_url || displayClinic.logo} 
                  alt={`${displayClinic.name_en || displayClinic.name_ar} logo`}
                  className="clinic-detail-modal-logo-img"
                />
              </div>
            </div>
          )}

          {/* Always show Basic Information section */}
          <div className="clinic-detail-modal-section">
            <h3 className="clinic-detail-modal-section-title">{t("adminDashboard.modal.sections.basicInformation")}</h3>
            <div className="clinic-detail-modal-grid">
              <div className="clinic-detail-modal-field">
                <label>{t("adminDashboard.modal.fields.registrationNumber")}</label>
                <span>{displayClinic.registration_number || t("adminDashboard.card.notAvailable")}</span>
              </div>
              <div className="clinic-detail-modal-field">
                <label>{t("adminDashboard.modal.fields.establishmentDate")}</label>
                <span>{displayClinic.establishment_date ? new Date(displayClinic.establishment_date).toLocaleDateString() : t("adminDashboard.card.notAvailable")}</span>
              </div>
              <div className="clinic-detail-modal-field">
                <label>{t("adminDashboard.modal.fields.vatNumber")}</label>
                <span>{displayClinic.vat_number || t("adminDashboard.card.notAvailable")}</span>
              </div>
              <div className="clinic-detail-modal-field">
                <label>{t("adminDashboard.modal.fields.clinicType")}</label>
                <span>
                  {(() => {
                    // Handle clinic_type as object with name_en/name_ar
                    if (displayClinic.clinic_type && typeof displayClinic.clinic_type === 'object' && !Array.isArray(displayClinic.clinic_type)) {
                      return displayClinic.clinic_type.name_en || 
                             displayClinic.clinic_type.name_ar || 
                             displayClinic.clinic_type.name ||
                             t("adminDashboard.card.notAvailable");
                    }
                    // Handle type_name or type as string
                    if (displayClinic.type_name) {
                      return displayClinic.type_name;
                    }
                    if (displayClinic.type && typeof displayClinic.type === 'string') {
                      return displayClinic.type;
                    }
                    // If it's just an ID (number), display the ID
                    const typeId = displayClinic.type || displayClinic.clinic_type;
                    if (typeof typeId === 'number') {
                      return typeId.toString();
                    }
                    return t("adminDashboard.card.notAvailable");
                  })()}
                </span>
              </div>
              <div className="clinic-detail-modal-field">
                <label>{t("adminDashboard.modal.fields.currency")}</label>
                <span>{displayClinic.currency || t("adminDashboard.card.notAvailable")}</span>
              </div>
              <div className="clinic-detail-modal-field">
                <label>{t("adminDashboard.modal.fields.invoiceStartingNumber")}</label>
                <span>{displayClinic.invoice_starting_number || displayClinic.invoice_start || t("adminDashboard.card.notAvailable")}</span>
              </div>
              <div className="clinic-detail-modal-field">
                <label>{t("adminDashboard.modal.fields.petStore")}</label>
                <span>{displayClinic.is_pet_store === true ? t("adminDashboard.modal.values.yes") : displayClinic.is_pet_store === false ? t("adminDashboard.modal.values.no") : t("adminDashboard.card.notAvailable")}</span>
              </div>
            </div>
          </div>

          <div className="clinic-detail-modal-section">
            <h3 className="clinic-detail-modal-section-title">{t("adminDashboard.modal.sections.contactInformation")}</h3>
            <div className="clinic-detail-modal-grid">
              <div className="clinic-detail-modal-field">
                <label>{t("adminDashboard.modal.fields.email")}</label>
                <span>{displayClinic.email || t("adminDashboard.card.notAvailable")}</span>
              </div>
              <div className="clinic-detail-modal-field">
                <label>{t("adminDashboard.modal.fields.telephone")}</label>
                <span>{displayClinic.telephone || t("adminDashboard.card.notAvailable")}</span>
              </div>
              <div className="clinic-detail-modal-field">
                <label>{t("adminDashboard.modal.fields.mobile")}</label>
                <span>{displayClinic.mobile || t("adminDashboard.card.notAvailable")}</span>
              </div>
              <div className="clinic-detail-modal-field">
                <label>{t("adminDashboard.modal.fields.website")}</label>
                <span>{displayClinic.website ? (
                  <a href={displayClinic.website} target="_blank" rel="noopener noreferrer">{displayClinic.website}</a>
                ) : t("adminDashboard.card.notAvailable")}</span>
              </div>
            </div>
          </div>

          <div className="clinic-detail-modal-section">
            <h3 className="clinic-detail-modal-section-title">{t("adminDashboard.modal.sections.location")}</h3>
            <div className="clinic-detail-modal-grid">
              <div className="clinic-detail-modal-field">
                <label>{t("adminDashboard.modal.fields.country")}</label>
                <span>
                  {typeof displayClinic.country === 'object' 
                    ? (displayClinic.country.name_en || displayClinic.country.name_ar || displayClinic.country.name)
                    : (displayClinic.country_name || displayClinic.country || t("adminDashboard.card.notAvailable"))}
                </span>
              </div>
              <div className="clinic-detail-modal-field">
                <label>{t("adminDashboard.modal.fields.city")}</label>
                <span>
                  {typeof displayClinic.city === 'object' 
                    ? (displayClinic.city.name_en || displayClinic.city.name_ar || displayClinic.city.name)
                    : (displayClinic.city_name || displayClinic.city || t("adminDashboard.card.notAvailable"))}
                </span>
              </div>
              <div className="clinic-detail-modal-field">
                <label>{t("adminDashboard.modal.fields.street")}</label>
                <span>{displayClinic.street || displayClinic.full_address || displayClinic.address || t("adminDashboard.card.notAvailable")}</span>
              </div>
              <div className="clinic-detail-modal-field">
                <label>{t("adminDashboard.modal.fields.buildingNumber")}</label>
                <span>{displayClinic.building_number || t("adminDashboard.card.notAvailable")}</span>
              </div>
              <div className="clinic-detail-modal-field">
                <label>{t("adminDashboard.modal.fields.floorNumber")}</label>
                <span>{displayClinic.floor_number || t("adminDashboard.card.notAvailable")}</span>
              </div>
              {(displayClinic.latitude && displayClinic.longitude) && (
                <div className="clinic-detail-modal-field">
                  <label>{t("adminDashboard.modal.fields.coordinates")}</label>
                  <span>{displayClinic.latitude}, {displayClinic.longitude}</span>
                </div>
              )}
            </div>
          </div>

          {displayClinic.description && (
            <div className="clinic-detail-modal-section">
              <h3 className="clinic-detail-modal-section-title">{t("adminDashboard.modal.sections.description")}</h3>
              <p className="clinic-detail-modal-description">{displayClinic.description}</p>
            </div>
          )}

          {displayClinic.services_offered && displayClinic.services_offered.length > 0 && (
            <div className="clinic-detail-modal-section">
              <h3 className="clinic-detail-modal-section-title">{t("adminDashboard.modal.sections.servicesOffered")}</h3>
              <div className="clinic-detail-modal-services">
                {displayClinic.services_offered.map((service, index) => (
                  <span key={index} className="clinic-detail-modal-service-tag">
                    {service.name || service}
                  </span>
                ))}
              </div>
            </div>
          )}

          {displayClinic.working_hours && Object.keys(displayClinic.working_hours).length > 0 && (
            <div className="clinic-detail-modal-section">
              <h3 className="clinic-detail-modal-section-title">{t("adminDashboard.modal.sections.workingHours")}</h3>
              <div className="clinic-detail-modal-working-hours">
                {Object.entries(displayClinic.working_hours).map(([day, hours]) => (
                  hours.enabled && (
                    <div key={day} className="clinic-detail-modal-working-hour">
                      <span className="clinic-detail-modal-working-day">{day}</span>
                      <span className="clinic-detail-modal-working-time">
                        {hours.from} - {hours.to}
                      </span>
                    </div>
                  )
                ))}
              </div>
            </div>
          )}

          {(() => {
            const owner = displayClinic.owner || displayClinic.user;
            const ownerName = owner 
              ? (owner.full_name || `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.name || owner.username || t("adminDashboard.card.notAvailable"))
              : t("adminDashboard.card.notAvailable");
            
            return (
              <div className="clinic-detail-modal-section">
                <h3 className="clinic-detail-modal-section-title">{t("adminDashboard.modal.sections.ownerInformation")}</h3>
                <div className="clinic-detail-modal-grid">
                  <div className="clinic-detail-modal-field">
                    <label>{t("adminDashboard.modal.fields.name")}</label>
                    <span>{ownerName}</span>
                  </div>
                  {owner && owner.email && (
                    <div className="clinic-detail-modal-field">
                      <label>{t("adminDashboard.modal.fields.email")}</label>
                      <span>{owner.email}</span>
                    </div>
                  )}
                  {owner && owner.username && (
                    <div className="clinic-detail-modal-field">
                      <label>{t("adminDashboard.modal.fields.username")}</label>
                      <span>{owner.username}</span>
                    </div>
                  )}
                  {owner && owner.phone && (
                    <div className="clinic-detail-modal-field">
                      <label>{t("adminDashboard.modal.fields.phone")}</label>
                      <span>{owner.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

        </div>

        {isUnverified && (
          <div className="clinic-detail-modal-actions">
            <button
              className="clinic-detail-modal-btn clinic-detail-modal-btn-reject"
              onClick={handleReject}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              {t("adminDashboard.modal.actions.reject")}
            </button>
            <button
              className="clinic-detail-modal-btn clinic-detail-modal-btn-verify"
              onClick={handleVerify}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              {t("adminDashboard.modal.actions.verifyClinic")}
            </button>
          </div>
        )}

        {isRejected && (
          <div className="clinic-detail-modal-actions">
            <button
              className="clinic-detail-modal-btn clinic-detail-modal-btn-verify"
              onClick={handleVerify}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              {t("adminDashboard.modal.actions.verifyClinic")}
            </button>
            <button
              className="clinic-detail-modal-btn clinic-detail-modal-btn-close"
              onClick={onClose}
            >
              {t("adminDashboard.modal.actions.close")}
            </button>
          </div>
        )}

        {isVerified && (
          <div className="clinic-detail-modal-actions">
            {!isPaid && hasUpgradeRequest ? (
              <button
                className="clinic-detail-modal-btn clinic-detail-modal-btn-upgrade"
                onClick={() => {
                  onUpgradeSubscription(displayClinic.id);
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
                {t("adminDashboard.modal.actions.upgradeToPaid")}
              </button>
            ) : isPaid ? (
              <button
                className="clinic-detail-modal-btn clinic-detail-modal-btn-downgrade"
                onClick={() => {
                  onDowngradeSubscription(displayClinic.id);
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22v-20M7 19H14.5a3.5 3.5 0 0 0 0-7h-5a3.5 3.5 0 0 1 0-7H18" />
                </svg>
                {t("adminDashboard.modal.actions.downgradeToFree")}
              </button>
            ) : null}
            <button
              className="clinic-detail-modal-btn clinic-detail-modal-btn-close"
              onClick={onClose}
            >
              {t("adminDashboard.modal.actions.close")}
            </button>
          </div>
        )}

        {!isUnverified && !isRejected && !isVerified && (
          <div className="clinic-detail-modal-actions">
            <button
              className="clinic-detail-modal-btn clinic-detail-modal-btn-close"
              onClick={onClose}
            >
              {t("adminDashboard.modal.actions.close")}
            </button>
          </div>
        )}

      </div>

      {showVerifyModal && (
        <VerifyClinicModal
          isOpen={showVerifyModal}
          onClose={() => setShowVerifyModal(false)}
          clinic={displayClinic}
          onConfirm={handleVerifyConfirm}
        />
      )}

      {showRejectModal && (
        <RejectClinicModal
          isOpen={showRejectModal}
          onClose={() => setShowRejectModal(false)}
          clinic={displayClinic}
          onConfirm={handleRejectConfirm}
        />
      )}
    </div>
  );
};

export default ClinicDetailModal;

